import { ReactElement, cloneElement } from "react";
import {
  Buildings,
  Warning,
  Calendar,
  FireExtinguisher,
  Lightbulb,
  Door,
  SignOut,
  Stairs,
  ClipboardText,
  Question,
} from "@phosphor-icons/react";
import { ActionableIssuesSummary } from "../hooks/useFireSafetyData";

interface SummaryCardProps {
  icon: ReactElement<any>;
  value: number;
  label: string;
  variant?: "default" | "warning" | "danger" | "muted";
  isActive?: boolean;
  onClick: () => void;
  tags?: Array<{ label: string; variant: "moss" | "brick" }>;
}

const SummaryCard = ({
  icon,
  value,
  label,
  variant = "default",
  isActive,
  onClick,
  tags = [],
}: SummaryCardProps) => {
  const iconWithProps = cloneElement(icon, {
    weight: "duotone" as const,
    size: 16,
    className: "flex-shrink-0",
  });

  // Determine colors based on variant
  const getValueColor = () => {
    switch (variant) {
      case "danger":
        return "text-brick-primary";
      case "warning":
        return "text-yellow-600";
      case "muted":
        return "text-neutral-secondary-mid";
      default:
        return "text-neutral-primary";
    }
  };

  return (
    <div
      className={`flex cursor-pointer flex-col gap-1.5 rounded-lg border p-3 transition-all duration-200 hover:shadow-sm ${
        isActive
          ? "border-moss-primary bg-moss-secondary-light ring-moss-primary ring-1"
          : "border-neutral-primary/15 hover:border-neutral-primary/30 bg-white"
      } `}
      onClick={onClick}
    >
      {/* Icon + Label row */}
      <div className="text-neutral-secondary-mid flex items-center gap-1.5">
        {iconWithProps}
        <span className="truncate text-xs font-medium tracking-wider uppercase">
          {label}
        </span>
      </div>

      {/* Value + Tags row */}
      <div className="flex items-center justify-between gap-2">
        <span className={`text-xl font-semibold ${getValueColor()}`}>
          {value}
        </span>
        {tags.length > 0 && (
          <div className="flex gap-1">
            {tags.map((tag, index) => (
              <span
                key={index}
                className={`rounded px-1.5 py-0.5 text-[10px] font-medium ${
                  tag.variant === "moss"
                    ? "bg-moss-primary/10 text-moss-primary"
                    : "bg-brick-primary/10 text-brick-primary"
                }`}
              >
                {tag.label}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

interface FireSafetySummaryCardsProps {
  actionableIssues: ActionableIssuesSummary;
  totalItems: number;
  activeFilter: string | null;
  onFilterClick: (filter: string, column?: string) => void;
}

export const FireSafetySummaryCards = ({
  actionableIssues,
  totalItems,
  activeFilter,
  onFilterClick,
}: FireSafetySummaryCardsProps) => {
  const bothTags = [
    { label: "G", variant: "moss" as const },
    { label: "U", variant: "brick" as const },
  ];
  const buildingTag = [{ label: "G", variant: "moss" as const }];

  return (
    <div className="mb-6">
      {/* Overview Section */}
      <div className="mb-4">
        <p className="text-neutral-secondary-mid mb-2 text-xs font-medium tracking-wider uppercase">
          Overzicht
        </p>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6">
          <SummaryCard
            icon={<Buildings />}
            value={totalItems}
            label="Totaal items"
            variant="default"
            onClick={() => onFilterClick("clear")}
            tags={bothTags}
          />
        </div>
      </div>

      {/* Actionable Issues Section */}
      <div className="mb-4">
        <p className="text-neutral-secondary-mid mb-2 text-xs font-medium tracking-wider uppercase">
          Urgent op te lossen
        </p>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-8">
          {/* Not OK items */}
          <SummaryCard
            icon={<Warning />}
            value={actionableIssues.rookmelders}
            label="Rookmelders"
            variant={actionableIssues.rookmelders > 0 ? "danger" : "default"}
            isActive={activeFilter === "rookmelders"}
            onClick={() => onFilterClick("rookmelders", "smokeDetectors")}
            tags={bothTags}
          />

          <SummaryCard
            icon={<FireExtinguisher />}
            value={actionableIssues.brandblussers}
            label="Brandblussers"
            variant={actionableIssues.brandblussers > 0 ? "danger" : "default"}
            isActive={activeFilter === "brandblussers"}
            onClick={() => onFilterClick("brandblussers", "fireExtinguishers")}
            tags={buildingTag}
          />

          <SummaryCard
            icon={<ClipboardText />}
            value={actionableIssues.brandhaspel}
            label="Brandhaspel"
            variant={actionableIssues.brandhaspel > 0 ? "danger" : "default"}
            isActive={activeFilter === "brandhaspel"}
            onClick={() => onFilterClick("brandhaspel", "fireHoseReel")}
            tags={buildingTag}
          />

          <SummaryCard
            icon={<Lightbulb />}
            value={actionableIssues.noodverlichting}
            label="Noodverlichting"
            variant={
              actionableIssues.noodverlichting > 0 ? "danger" : "default"
            }
            isActive={activeFilter === "noodverlichting"}
            onClick={() =>
              onFilterClick("noodverlichting", "emergencyLighting")
            }
            tags={buildingTag}
          />

          <SummaryCard
            icon={<Door />}
            value={actionableIssues.branddeuren}
            label="Branddeuren"
            variant={actionableIssues.branddeuren > 0 ? "danger" : "default"}
            isActive={activeFilter === "branddeuren"}
            onClick={() => onFilterClick("branddeuren", "fireProofDoors")}
            tags={bothTags}
          />

          <SummaryCard
            icon={<SignOut />}
            value={actionableIssues.evacuatie}
            label="Evacuatie"
            variant={actionableIssues.evacuatie > 0 ? "danger" : "default"}
            isActive={activeFilter === "evacuatie"}
            onClick={() => onFilterClick("evacuatie", "fireEvacuation")}
            tags={buildingTag}
          />

          <SummaryCard
            icon={<Stairs />}
            value={actionableIssues.brandtrap}
            label="Brandtrap"
            variant={actionableIssues.brandtrap > 0 ? "danger" : "default"}
            isActive={activeFilter === "brandtrap"}
            onClick={() => onFilterClick("brandtrap", "fireStair")}
            tags={buildingTag}
          />
        </div>
      </div>

      {/* Inspection Dates Section */}
      <div className="mb-4">
        <p className="text-neutral-secondary-mid mb-2 text-xs font-medium tracking-wider uppercase">
          Keuringen
        </p>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6">
          <SummaryCard
            icon={<Calendar />}
            value={actionableIssues.keuringVervallen}
            label="Vervallen"
            variant={
              actionableIssues.keuringVervallen > 0 ? "danger" : "default"
            }
            isActive={activeFilter === "keuringVervallen"}
            onClick={() => onFilterClick("keuringVervallen", "nextInspection")}
            tags={bothTags}
          />

          <SummaryCard
            icon={<Calendar />}
            value={actionableIssues.keuring}
            label="Binnenkort"
            variant={actionableIssues.keuring > 0 ? "warning" : "default"}
            isActive={activeFilter === "keuring"}
            onClick={() => onFilterClick("keuring", "nextInspection")}
            tags={bothTags}
          />

          <SummaryCard
            icon={<Calendar />}
            value={actionableIssues.inspectiedatumNietIngepland}
            label="Niet ingepland"
            variant="muted"
            isActive={activeFilter === "inspectiedatumNietIngepland"}
            onClick={() =>
              onFilterClick("inspectiedatumNietIngepland", "nextInspection")
            }
            tags={bothTags}
          />
        </div>
      </div>

      {/* Unknown Status Section */}
      <div>
        <p className="text-neutral-secondary-mid mb-2 text-xs font-medium tracking-wider uppercase">
          Na te kijken en aan te vullen
        </p>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-8">
          <SummaryCard
            icon={<Question />}
            value={actionableIssues.rookmeldersOnbekend}
            label="Rookmelders"
            variant="muted"
            isActive={activeFilter === "rookmeldersOnbekend"}
            onClick={() =>
              onFilterClick("rookmeldersOnbekend", "smokeDetectors")
            }
            tags={bothTags}
          />

          <SummaryCard
            icon={<Question />}
            value={actionableIssues.brandblusersOnbekend}
            label="Brandblussers"
            variant="muted"
            isActive={activeFilter === "brandblusersOnbekend"}
            onClick={() =>
              onFilterClick("brandblusersOnbekend", "fireExtinguishers")
            }
            tags={buildingTag}
          />

          <SummaryCard
            icon={<Question />}
            value={actionableIssues.brandhaspelOnbekend}
            label="Brandhaspel"
            variant="muted"
            isActive={activeFilter === "brandhaspelOnbekend"}
            onClick={() => onFilterClick("brandhaspelOnbekend", "fireHoseReel")}
            tags={buildingTag}
          />

          <SummaryCard
            icon={<Question />}
            value={actionableIssues.noodverlichtingOnbekend}
            label="Noodverlichting"
            variant="muted"
            isActive={activeFilter === "noodverlichtingOnbekend"}
            onClick={() =>
              onFilterClick("noodverlichtingOnbekend", "emergencyLighting")
            }
            tags={buildingTag}
          />

          <SummaryCard
            icon={<Question />}
            value={actionableIssues.brandeurenOnbekend}
            label="Branddeuren"
            variant="muted"
            isActive={activeFilter === "brandeurenOnbekend"}
            onClick={() =>
              onFilterClick("brandeurenOnbekend", "fireProofDoors")
            }
            tags={bothTags}
          />

          <SummaryCard
            icon={<Question />}
            value={actionableIssues.evacuatieOnbekend}
            label="Evacuatie"
            variant="muted"
            isActive={activeFilter === "evacuatieOnbekend"}
            onClick={() => onFilterClick("evacuatieOnbekend", "fireEvacuation")}
            tags={buildingTag}
          />

          <SummaryCard
            icon={<Question />}
            value={actionableIssues.brandtrapOnbekend}
            label="Brandtrap"
            variant="muted"
            isActive={activeFilter === "brandtrapOnbekend"}
            onClick={() => onFilterClick("brandtrapOnbekend", "fireStair")}
            tags={buildingTag}
          />
        </div>
      </div>
    </div>
  );
};
